import { useState, type FormEvent } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { ArrowDownIcon, ArrowUpIcon, ChevronIcon, CloseIcon, EditIcon, EmptyState, ErrorState, InstagramIcon, LoadingState, PlusIcon, SearchIcon, Segments, Spark, StarIcon, useConfirm, useToast } from "@signet/ui";
import { CreatorAvatar, useCreatorPhotos, useMe } from "@signet/web-kit";
import { handleFrom } from "../../research/shared/handle.ts";
import { periodOf, PERIODS, type PeriodId } from "../shared/period.ts";
import type { RowRun } from "../shared/redownload.ts";
import { addAccount, BASE, downloadKeys, fetchDownloadCounts, fetchOverview, fetchRedownloadRows, keys, redownloadKeys, removeAccount, setMain, type AccountRow, type CreatorRow } from "./api.ts";
import { DownloadCell, DownloadsModal } from "./downloads.tsx";
import { RedownloadButton, RedownloadModal } from "./redownload.tsx";
import { MainAccountsTabs } from "./tabs.tsx";

/* ──────────────────────────────────────────────────────────────────────────
   Main Accounts: one row per creator, posts in the period on all their main
   accounts, the change against the period before, and posts per day for the
   last week. A row opens to its accounts, and to Edit accounts for people who
   may change them.
   ────────────────────────────────────────────────────────────────────────── */

const compact = new Intl.NumberFormat("en", { notation: "compact", maximumFractionDigits: 1 });

function Change({ posts, previous }: { posts: number; previous: number }) {
  const diff = posts - previous;
  if (diff === 0) return <span className="ma-change same">No change</span>;
  const up = diff > 0;
  return (
    <span className={`ma-change ${up ? "up" : "down"}`} title={`${previous.toLocaleString()} the period before`}>
      {up ? <ArrowUpIcon size={12} /> : <ArrowDownIcon size={12} />}
      {Math.abs(diff).toLocaleString()}
    </span>
  );
}

const sparkLabel = (spark: number[]) => `${spark.reduce((a, b) => a + b, 0)} posts in the last 7 days`;

function Status({ account }: { account: AccountRow }) {
  if (account.status === "private") return <span className="ma-tag warn">Private</span>;
  if (account.status === "missing") return <span className="ma-tag bad" title="Instagram shows nothing for it. It's checked again every day.">Banned</span>;
  if (account.reading) return <span className="ma-tag">Reading the last 2 months</span>;
  return null;
}

/**
 * The "main main" account's star (Ben, 2026-10-28). People who may edit
 * accounts click any star to make that account the main one; clicking the
 * picked one again goes back to the one with the most followers.
 */
function MainStar({ row, account, canEdit }: { row: CreatorRow; account: AccountRow; canEdit: boolean }) {
  const client = useQueryClient();
  const toast = useToast();
  const pick = useMutation({
    mutationFn: () => setMain(row.creator, account.main && row.mainPicked ? null : account.id),
    onSuccess: () => client.invalidateQueries({ queryKey: keys.all }),
    onError: (error: Error) => toast.error("Couldn't change the main account", error.message),
  });
  const followers = account.followers === null ? "" : `${compact.format(account.followers)} followers. `;
  if (!canEdit) {
    return account.main ? (
      <span className="ma-star on" title={`${followers}Main account`}>
        <StarIcon filled size={12} />
      </span>
    ) : (
      <span className="ma-star" aria-hidden="true" />
    );
  }
  const title = account.main
    ? `${followers}Main account${row.mainPicked ? ", picked by hand. Click to go back to the one with the most followers." : ", the one with the most followers."}`
    : `${followers}Make @${account.handle} the main account`;
  return (
    <button type="button" className={`ma-star${account.main ? " on" : ""}`} title={title} aria-label={title} aria-pressed={account.main} disabled={pick.isPending} onClick={() => pick.mutate()}>
      <StarIcon filled={account.main} size={12} />
    </button>
  );
}

function AccountLine({ row, account, canEdit, withDownloaded }: { row: CreatorRow; account: AccountRow; canEdit: boolean; withDownloaded: boolean }) {
  return (
    <div className="ma-account">
      <MainStar row={row} account={account} canEdit={canEdit} />
      <span className="ma-who">
        <a className="ma-handle" href={`https://www.instagram.com/${account.handle}/`} target="_blank" rel="noreferrer noopener">
          <InstagramIcon size={13} />@{account.handle}
        </a>
        <Status account={account} />
      </span>
      <span />
      <span className="ma-num">{account.posts.toLocaleString()}</span>
      {withDownloaded ? <span className="ma-got" /> : null}
      <Change posts={account.posts} previous={account.previous} />
      <Spark values={account.spark} label={sparkLabel(account.spark)} />
      <span className="ma-views" title="Play counts of the Reels posted in the period, as last read">
        {account.views ? `${compact.format(account.views)} views` : "No views"}
      </span>
    </div>
  );
}

function EditAccounts({ row, onDone }: { row: CreatorRow; onDone: () => void }) {
  const client = useQueryClient();
  const toast = useToast();
  const { confirm, element: confirmation } = useConfirm();
  const [typed, setTyped] = useState("");
  const handle = handleFrom(typed);
  const refresh = () => client.invalidateQueries({ queryKey: keys.all });

  const add = useMutation({
    mutationFn: () => addAccount(row.creator, typed),
    onSuccess: (r) => {
      setTyped("");
      refresh();
      toast.success(
        `Added @${r.handle}`,
        r.banned ? "It's banned, so it's marked Banned. It's checked every day and read once it's back." : r.private ? "It's private, so its posts can't be read." : "Its last 2 months of posts are being read now.",
      );
    },
    onError: (error: Error) => toast.error("Couldn't add that account", error.message),
  });

  const remove = useMutation({
    mutationFn: (id: string) => removeAccount(id),
    onSuccess: () => refresh(),
    onError: (error: Error) => toast.error("Couldn't remove it", error.message),
  });

  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (handle && !add.isPending) add.mutate();
  };

  const askRemove = async (account: AccountRow) => {
    const yes = await confirm({ title: `Remove @${account.handle}?`, body: `It stops counting towards ${row.creator}. Its posts are kept, so adding it back later is free.`, confirmLabel: "Remove", danger: true });
    if (yes) remove.mutate(account.id);
  };

  return (
    <div className="ma-edit">
      {row.accounts.map((a) => (
        <div key={a.id} className="ma-edit-line">
          <span className="ma-handle">
            <InstagramIcon size={13} />@{a.handle}
          </span>
          <button type="button" className="icon-button" aria-label={`Remove @${a.handle}`} disabled={remove.isPending} onClick={() => askRemove(a)}>
            <CloseIcon size={14} />
          </button>
        </div>
      ))}
      <form className="ma-add" onSubmit={submit}>
        <label className="input-prefix">
          <span>@</span>
          <input value={typed} onChange={(e) => setTyped(e.target.value)} placeholder="Handle or profile link" aria-label="Instagram handle or profile link" autoFocus />
        </label>
        <button type="submit" className="button" disabled={!handle || add.isPending}>
          <PlusIcon size={14} /> {add.isPending ? "Adding" : "Add"}
        </button>
        <button type="button" className="text-button" onClick={onDone}>
          Done
        </button>
      </form>
      {typed.trim() && !handle ? <p className="ma-hint">That isn't an Instagram handle or profile link.</p> : null}
      {confirmation}
    </div>
  );
}

type Downloads = { folders: number; creatorUploads: boolean; downloaded: number; canView: boolean; canEdit: boolean; canRedownload: boolean; run: RowRun | null };

/** How many of the period's posts are in Drive (Ben, 2026-10-29), for a creator whose posts go to a folder. */
function Downloaded({ posts, downloads }: { posts: number; downloads: Downloads }) {
  if (!downloads.canView || downloads.folders === 0) return <span className="ma-got" />;
  const title = `${downloads.downloaded.toLocaleString()} of the ${posts.toLocaleString()} ${posts === 1 ? "post" : "posts"} in the period ${downloads.downloaded === 1 ? "is" : "are"} in Google Drive. A folder set to Videos skips photo posts.`;
  return (
    <span className="ma-num ma-got" title={title}>
      {downloads.downloaded.toLocaleString()}
      <small> downloaded</small>
    </span>
  );
}

function CreatorLine({ row, photo, open, onToggle, canEdit, downloads, withDownloaded }: { row: CreatorRow; photo: string | null; open: boolean; onToggle: () => void; canEdit: boolean; downloads: Downloads; withDownloaded: boolean }) {
  const [editing, setEditing] = useState(false);
  const [setup, setSetup] = useState(false);
  const [again, setAgain] = useState(false);
  const none = row.accounts.length === 0;
  // Re-Download reads the creator's accounts, so a creator with none has nothing to offer.
  const redownload = downloads.canRedownload && !none;
  return (
    <div className={`ma-row${open ? " open" : ""}`}>
      {/* The toggle is the chevron and name; the rest of the row opens it too, by mouse, around the download cell's own buttons. */}
      <div className="ma-line" onClick={onToggle}>
        <button
          type="button"
          className="ma-toggle"
          aria-expanded={open}
          onClick={(e) => {
            e.stopPropagation();
            onToggle();
          }}
        >
          <ChevronIcon size={14} direction={open ? "down" : "right"} />
          <span className="ma-creator">
            <CreatorAvatar name={row.creator} src={photo} size={26} />
            <b>{row.creator}</b>
          </span>
        </button>
        <span className="ma-dl-cell">
          {downloads.canView && (downloads.folders > 0 || downloads.creatorUploads || downloads.canEdit) ? (
            <DownloadCell folders={downloads.folders} creatorUploads={downloads.creatorUploads} canEdit={downloads.canEdit} onOpen={() => setSetup(true)} />
          ) : null}
          {redownload ? <RedownloadButton creator={row.creator} run={downloads.run} onOpen={() => setAgain(true)} /> : null}
        </span>
        {none ? (
          <span className="ma-none">No accounts</span>
        ) : (
          <>
            <span className="ma-num">{row.posts.toLocaleString()}</span>
            {withDownloaded ? <Downloaded posts={row.posts} downloads={downloads} /> : null}
            <Change posts={row.posts} previous={row.previous} />
            <Spark values={row.spark} label={sparkLabel(row.spark)} />
          </>
        )}
      </div>
      {downloads.canEdit ? <DownloadsModal creator={row.creator} open={setup} onClose={() => setSetup(false)} /> : null}
      {redownload ? <RedownloadModal creator={row.creator} run={downloads.run} open={again} onClose={() => setAgain(false)} /> : null}
      {open ? (
        <div className="ma-detail">
          {editing ? (
            <div className="ma-edit-wrap">
              <EditAccounts row={row} onDone={() => setEditing(false)} />
            </div>
          ) : (
            <>
              {row.accounts.map((a) => (
                <AccountLine key={a.id} row={row} account={a} canEdit={canEdit} withDownloaded={withDownloaded} />
              ))}
              {none ? <p className="ma-hint">No main accounts added for {row.creator} yet.</p> : null}
              {canEdit ? (
                <button type="button" className="secondary-button ma-edit-button" onClick={() => setEditing(true)}>
                  <EditIcon size={14} /> Edit accounts
                </button>
              ) : null}
            </>
          )}
        </div>
      ) : null}
    </div>
  );
}

export function MainAccountsPage() {
  const { can } = useMe();
  const search = useSearch({ strict: false }) as { period?: string };
  const navigate = useNavigate();
  const period = periodOf(search.period);
  const photos = useCreatorPhotos(BASE);
  const [open, setOpen] = useState<string | null>(null);
  const [showNone, setShowNone] = useState(false);
  const [query, setQuery] = useState("");
  const canViewDownloads = can("mainaccounts.download:view");
  const counts = useQuery({ queryKey: downloadKeys.counts(period.id), queryFn: () => fetchDownloadCounts(period.id), enabled: canViewDownloads, placeholderData: (previous) => previous });
  const canRedownload = can("mainaccounts.redownload:create");
  // Each row's latest Re-Download, polled while any is running so In progress turns to Completed by itself.
  const runs = useQuery({ queryKey: redownloadKeys.rows, queryFn: fetchRedownloadRows, enabled: canRedownload, refetchInterval: (q) => (Object.values(q.state.data?.runs ?? {}).some((r) => r.state === "running") ? 15_000 : false) });
  const data = useQuery({ queryKey: keys.overview(period.id), queryFn: () => fetchOverview(period.id), refetchInterval: (q) => (q.state.data?.creators.some((c) => c.accounts.some((a) => a.reading)) ? 15_000 : false) });

  const setPeriod = (id: PeriodId) => navigate({ to: "/main-accounts" as never, search: (id === "today" ? {} : { period: id }) as never, replace: true });

  const line = (row: CreatorRow) => (
    <CreatorLine
      key={row.creator}
      row={row}
      photo={photos(row.creator)}
      open={open === row.creator}
      onToggle={() => setOpen(open === row.creator ? null : row.creator)}
      canEdit={can("mainaccounts.account:edit")}
      downloads={{
        folders: counts.data?.folders[row.creator] ?? 0,
        creatorUploads: counts.data?.creatorUploads.includes(row.creator) ?? false,
        downloaded: counts.data?.downloaded[row.creator] ?? 0,
        canView: canViewDownloads && counts.isSuccess,
        canEdit: can("mainaccounts.download:edit"),
        canRedownload,
        run: runs.data?.runs[row.creator.toUpperCase()] ?? null,
      }}
      withDownloaded={canViewDownloads}
    />
  );
  // Jay, 2026-10-20: creators with no accounts yet go in one dropdown under the rest, not one row each among them.
  const wanted = query.trim().toUpperCase();
  const creators = (data.data?.creators ?? []).filter((row) => !wanted || row.creator.toUpperCase().includes(wanted));
  const tracked = creators.filter((row) => row.accounts.length > 0);
  const untracked = creators.filter((row) => row.accounts.length === 0);

  return (
    <div className="page ma-page">
      <MainAccountsTabs />
      <header className="page-head">
        <div>
          <h1>Main Accounts</h1>
          <p>Reels and feed posts on each creator's main Instagram accounts, read once a day. Click a creator to see each account.</p>
        </div>
        <div className="page-head-actions">
          <label className="ma-search">
            <SearchIcon size={13} />
            <input type="search" value={query} placeholder="Search creators" aria-label="Search creators" onChange={(e) => setQuery(e.target.value)} />
          </label>
          <Segments label="Period" value={period.id} options={PERIODS.map((p) => ({ value: p.id, label: p.label }))} onChange={setPeriod} />
        </div>
      </header>

      {data.isLoading ? (
        <LoadingState />
      ) : data.isError || !data.data ? (
        <ErrorState title="Couldn't load Main Accounts" onRetry={() => data.refetch()} />
      ) : !data.data.creators.length ? (
        <EmptyState title="No creators on the roster" />
      ) : (
        <div className={`panel flat ma-list${canRedownload ? " ma-with-rd" : ""}${canViewDownloads ? " ma-with-got" : ""}`}>
          <div className="ma-head" aria-hidden="true">
            <span />
            <span>Creator</span>
            <span />
            <span>Posts</span>
            {canViewDownloads ? <span>Downloaded</span> : null}
            <span>{period.versus}</span>
            <span>Last 7 days</span>
          </div>
          {tracked.map(line)}
          {wanted && !creators.length ? <p className="ma-hint ma-no-match">No creator matches "{query.trim()}".</p> : null}
          {untracked.length ? (
            <div className={`ma-group${showNone || wanted ? " open" : ""}`}>
              <button type="button" className="ma-group-line" aria-expanded={showNone || Boolean(wanted)} onClick={() => setShowNone(!showNone)}>
                <ChevronIcon size={14} direction={showNone || wanted ? "down" : "right"} />
                No accounts yet
              </button>
              {showNone || wanted ? <div className="ma-group-list">{untracked.map(line)}</div> : null}
            </div>
          ) : null}
        </div>
      )}
    </div>
  );
}
